import type { RelatedItemState } from '#layers/blocks/components/Relation/related-item-state';
import { computed, ref } from 'vue';
import { RelatedItemStates } from '#layers/blocks/components/Relation/related-item-state';

interface UsePendingConnectionsOptions {
	/** Returns whether an assignment is already present in the loaded persisted pages. */
	isPersisted?: (id: string) => boolean;
}

/**
 * Track staged assignment additions and removals independently of loaded persisted pages.
 *
 * Persisted membership remains the responsibility of the server query. This state only records
 * user intent, so callers do not need to load every assigned id before paginating the list.
 */
export function usePendingConnections(options: UsePendingConnectionsOptions = {}) {
	const pendingConnections = ref(new Set<string>());
	const pendingDisconnections = ref(new Set<string>());

	const hasChanges = computed(() => pendingConnections.value.size > 0 || pendingDisconnections.value.size > 0);

	/** Stage an addition, or cancel a staged removal of the same assignment. Returns whether a new addition was staged. */
	function connect(id: string): boolean {
		if (pendingDisconnections.value.delete(id)) return false;
		if (options.isPersisted?.(id)) return false;
		pendingConnections.value.add(id);
		return true;
	}

	/** Stage a removal, or drop a not-yet-saved addition of the same assignment. */
	function disconnect(id: string) {
		if (pendingConnections.value.delete(id)) return;
		pendingDisconnections.value.add(id);
	}

	/** Clear either staged operation for an assignment. */
	function undo(id: string) {
		pendingConnections.value.delete(id);
		pendingDisconnections.value.delete(id);
	}

	/** Resolve the list-card state for an assignment row. */
	function getItemState(id: string): RelatedItemState {
		if (pendingConnections.value.has(id)) return RelatedItemStates.Connected;
		if (pendingDisconnections.value.has(id)) return RelatedItemStates.Disconnected;
		return RelatedItemStates.Default;
	}

	function captureChanges() {
		const connections = pendingConnections.value;
		const disconnections = pendingDisconnections.value;
		return {
			connections: [...connections],
			disconnections: [...disconnections],
			acknowledgeConnections(ids: string[]) {
				if (pendingConnections.value !== connections) return;

				for (const id of ids) {
					// Undo during a save now requires a removal against the new persisted baseline.
					if (!connections.delete(id)) disconnections.add(id);
				}
			},
			acknowledgeDisconnections(ids: string[]) {
				if (pendingDisconnections.value !== disconnections) return;

				for (const id of ids) {
					if (!disconnections.delete(id)) connections.add(id);
				}
			},
		};
	}

	/** Clear every staged assignment operation when the owning target changes. */
	function clear() {
		pendingConnections.value = new Set();
		pendingDisconnections.value = new Set();
	}

	return {
		pendingConnections,
		pendingDisconnections,
		hasChanges,
		connect,
		disconnect,
		undo,
		getItemState,
		captureChanges,
		clear,
	};
}
